import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { useParams } from 'react-router';
import { Box, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import Close from '@mui/icons-material/Close';
import { AxiosResponse } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { useQuery } from 'src/hooks/useQuery';
import * as Yup from 'yup';
import { CountryCode } from 'libphonenumber-js';
import { MerchantTitle } from 'app/MerchantTitle';
import { getIdentityLabel } from 'components/settings/model/Settings';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import { PasswordFormField } from 'lib/form/PasswordFormField';
import { Link } from 'lib/Link';
import { Throbber } from 'lib/Throbber';
import { useLocalHistory } from 'lib/useLocalHistory';
import { BaseRouteParams, ROUTES } from 'pages/routes';
import { signIn } from 'store/auth/authSlice';
import { resetRewards } from 'store/basket/basketSlice';
import { ApplicationState } from 'store/store';
import { LoadingButton } from 'ui/LoadingButton';
import { UserData, formatPhoneAsE164, getIdentityValidationScheme } from './model/User';
import { getOnCloseRedirectUrl } from './localAuth';
import { GoogleSSO } from './GoogleSSO';
import { AppleSSO } from './AppleSSO';
import { TermsAndConditions } from './TermsAndCondition';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { CountryCodePicker, getCountryOptions, getDefaultCountry } from './CountryCodePicker';
import { IdentitySelector } from './IdentitySelector';

const PREFIX = 'LogIn';

const classes = {
    button: `${PREFIX}button`,
    forgot: `${PREFIX}forgot`,
    forgotWith2FA: `${PREFIX}forgotWith2FA`,
    orContainer: `${PREFIX}orContainer`,
    or: `${PREFIX}or`
};

const StyledGrid = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(2),
    [`& .${classes.button}`]: {
        height: '48px',
        fontSize: '1.2em'
    },
    [`& .${classes.forgot}`]: {
        margin: theme.spacing(0, 1.75),
        marginTop: theme.spacing(1),
        display: 'block'
    },
    [`& .${classes.forgotWith2FA}`]: {
        margin: theme.spacing(0, 1.75),
        marginTop: theme.spacing(-1),
        display: 'block'
    },
    [`& .${classes.orContainer}`]: {
        position: 'relative',
        padding: theme.spacing(0, 5),
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2)
    },
    [`& .${classes.or}`]: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(1, 3),
        color: theme.palette.divider
    }
}));

Yup.addMethod(Yup.string, 'or', function (schemas: Yup.Schema[], msg: string) {
    // TODO: find better way to do this
    return (this as unknown as any).test({
        name: 'or',
        message: msg || 'Please enter valid url or email.',
        test: (value: string) => {
            if (Array.isArray(schemas) && schemas.length > 1) {
                const resee = schemas.map(schema => schema.isValidSync(value));
                return resee.some(res => res);
            }
            throw new TypeError('Schemas is not correct array schema');
        },
        exclusive: false
    });
});

interface LogInProps {
    message?: string;
}

export interface LoginFormData {
    identity: string;
    email: string;
    phone: string;
    password: string;
}

export type LoginTab = 'phone' | 'email';

export const LogIn: React.FC<React.PropsWithChildren<LogInProps>> = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { merchantId } = useParams<BaseRouteParams>();
    const { logUserEvent } = useGAHelpers();
    const userIdentity = useQuery('identity');
    const { settings, isLoading } = useSelector((state: ApplicationState) => state.settings);
    const [errorMessage, setErrorMessage] = React.useState('');
    const { isLoading: isAuthLoading } = useSelector((state: ApplicationState) => state.auth);
    const [selectedCountry, setSelectedCountry] = React.useState<string>(() =>
        getDefaultCountry(
            settings?.phoneAuth?.supportedCountryCallingCodes ?? [],
            settings?.region?.phoneNumberCode
        )
    );
    const [activeTab, setActiveTab] = React.useState<LoginTab>('phone');
    const { push, getParsedPath } = useLocalHistory();

    const identity = React.useMemo(
        () => getIdentityLabel(settings?.emailAuthEnabled, settings?.phoneAuthEnabled),
        [settings?.emailAuthEnabled, settings?.phoneAuthEnabled]
    );

    const countryOptions = React.useMemo(
        () => getCountryOptions(settings?.phoneAuth?.supportedCountryCallingCodes ?? []),
        [settings?.phoneAuth?.supportedCountryCallingCodes]
    );

    const hasMultipleCountryCodes = countryOptions.length > 1;

    // both enabled + multiple country codes - show tab toggle
    const useSeparateInputs = identity === 'both' && settings?.phoneAuthEnabled && hasMultipleCountryCodes;

    const showCountryInfo = identity === 'phone' || useSeparateInputs;

    const initialFields: LoginFormData = React.useMemo(
        () => ({
            identity: userIdentity ?? '',
            email: '',
            phone: '',
            password: ''
        }),
        [userIdentity]
    );

    const has2FA = React.useMemo(
        () => !!settings && !!settings.iam && settings.iam.appUserAuth === 'VERIFY_PASSWORD',
        [settings]
    );

    const handleCountryChange = React.useCallback(
        (setFieldValue: FormikHelpers<LoginFormData>['setFieldValue']) => (value: string) => {
            setSelectedCountry(value);
            if (useSeparateInputs) {
                setFieldValue('phone', '');
            } else {
                setFieldValue('identity', '');
            }
        },
        [useSeparateInputs]
    );
    const handleTabChange = React.useCallback(
        (setFieldValue: FormikHelpers<LoginFormData>['setFieldValue']) => (newTab: LoginTab) => {
            setActiveTab(newTab);
            setFieldValue('email', '');
            setFieldValue('phone', '');
        },
        []
    );
    const signinValidation = React.useMemo(() => {
        if (useSeparateInputs) {
            return Yup.object().shape({
                ...(activeTab === 'email' && {
                    email: getIdentityValidationScheme('email', selectedCountry as CountryCode, t)
                }),
                ...(activeTab === 'phone' && {
                    phone: getIdentityValidationScheme('phone', selectedCountry as CountryCode, t)
                }),
                ...(!has2FA && {
                    password: Yup.string().required(t('FORMS_VALIDATION_REQUIRED'))
                })
            });
        }

        return Yup.object().shape({
            identity: getIdentityValidationScheme(identity, selectedCountry as CountryCode, t),
            ...(!has2FA && {
                password: Yup.string().required(t('FORMS_VALIDATION_REQUIRED'))
            })
        });
    }, [t, identity, selectedCountry, has2FA, useSeparateInputs, activeTab]);

    const handleSignInSuccess = React.useCallback(
        (res: UserData) => {
            dispatch(resetRewards());
            const returnUrl = getOnCloseRedirectUrl(merchantId ?? '');
            if (returnUrl) {
                const [path, search] = returnUrl.split('?');
                push(path, {}, search);
            } else {
                push(ROUTES.BASE);
            }

            return res;
        },
        [dispatch, merchantId, push]
    );

    const handleSignInError = React.useCallback(
        (error: AxiosResponse<{ message: string; code: string }>) => {
            if (error.data?.code === 'E-WAF-0001') {
                return setErrorMessage(error.data.message);
            }
            switch (error.status) {
                case 401:
                    setErrorMessage("Sorry! We couldn't find you with that email and password combo.");
                    break;
                default:
                    setErrorMessage(t('GENERAL_ERROR_PROCESSING_REQUEST'));
                    break;
            }
        },
        [t]
    );

    const handleSubmit = React.useCallback(
        (data: LoginFormData) => {
            logUserEvent(EGAEventName.LoginSubmit);
            let identityValue: string;
            let isPhoneInput: boolean;
            if (useSeparateInputs) {
                identityValue = activeTab === 'email' ? data.email : data.phone;
                isPhoneInput = activeTab === 'phone';
            } else {
                identityValue = data.identity;
                isPhoneInput = identity === 'phone';
            }
            if (isPhoneInput) {
                identityValue = formatPhoneAsE164(identityValue, selectedCountry as CountryCode);
            }
            if (has2FA) {
                push(getParsedPath(ROUTES.USER.TWO_FACTOR_AUTHENTICATION), undefined, undefined, {
                    id: identityValue
                });
            } else {
                const credentials = btoa(`${identityValue}:${data.password}`);

                return dispatch(signIn({ credentials }))
                    .unwrap()
                    .then(handleSignInSuccess)
                    .catch(handleSignInError);
            }
        },
        [
            dispatch,
            getParsedPath,
            handleSignInError,
            handleSignInSuccess,
            has2FA,
            logUserEvent,
            push,
            useSeparateInputs,
            activeTab,
            selectedCountry,
            identity
        ]
    );

    const handleKeyPress = React.useCallback(
        (submit: () => Promise<void>) => (e: React.KeyboardEvent) => {
            if (e.keyCode === 13) {
                submit();
            }
        },
        []
    );

    const handleForgotClick = React.useCallback(() => {
        logUserEvent(EGAEventName.ForgotPasswordClick);
    }, [logUserEvent]);

    if (isLoading) {
        return <Throbber text={t('GENERAL_LOADING')} />;
    }

    return (
        <>
            <MerchantTitle title={t('ONBOARDING_SIGN_IN')} />
            <MuiAppBar title={t('ONBOARDING_SIGN_IN')} icon={Close} disabledMenu />
            <Formik initialValues={initialFields} validationSchema={signinValidation} onSubmit={handleSubmit}>
                {({
                    submitForm,
                    setFieldValue,
                    isSubmitting,
                    isValid,
                    values: { identity: identityField, email, phone, password }
                }) => {
                    const countryPicker = (
                        <CountryCodePicker
                            selectedCountry={selectedCountry}
                            onCountryChange={handleCountryChange(setFieldValue)}
                            supportedCodes={settings?.phoneAuth?.supportedCountryCallingCodes ?? []}
                        />
                    );
                    const isActiveTabFieldEmpty = activeTab === 'email' ? !email : !phone;
                    const isIdentityEmpty = useSeparateInputs ? isActiveTabFieldEmpty : !identityField;

                    return (
                        <Form>
                            <StyledGrid container spacing={2}>
                                <Grid size={12}>
                                    <Typography align="center">
                                        {t('AUTH_NO_ACCOUNT') + ' '}
                                        <Link to={getParsedPath(ROUTES.USER.REGISTER)}>
                                            {t('ONBOARDING_SIGN_UP')}
                                        </Link>
                                    </Typography>
                                </Grid>
                                <GoogleSSO
                                    onSignInError={handleSignInError}
                                    onSignInSuccess={handleSignInSuccess}
                                />
                                <AppleSSO
                                    onSignInError={handleSignInError}
                                    onSignInSuccess={handleSignInSuccess}
                                />
                                {(settings?.appleSSOEnabled || settings?.googleSSOEnabled) && (
                                    <Grid size={12}>
                                        <Box className={classes.orContainer}>
                                            <Divider />
                                            <Typography className={classes.or}>OR</Typography>
                                        </Box>
                                    </Grid>
                                )}
                                <IdentitySelector
                                    useSeparateInputs={!!useSeparateInputs}
                                    identity={identity}
                                    activeTab={activeTab}
                                    onTabChange={handleTabChange(setFieldValue)}
                                    countryPicker={countryPicker}
                                    showCountryInfo={!!showCountryInfo}
                                />
                                {!has2FA && (
                                    <Grid size={12}>
                                        <PasswordFormField
                                            name="password"
                                            label={t('ONBOARDING_SIGNIN_PASSWORD')}
                                            onKeyDown={handleKeyPress(submitForm)}
                                        />
                                        <Typography
                                            align="right"
                                            variant="caption"
                                            className={classes.forgot}
                                            onClick={handleForgotClick}
                                        >
                                            <Link to={getParsedPath(ROUTES.USER.FORGOT_PASSWORD)}>
                                                {t('BUTTON_FORGOTTEN')}
                                            </Link>
                                        </Typography>
                                    </Grid>
                                )}
                                {has2FA && (
                                    <Grid size={12}>
                                        <Typography
                                            align="right"
                                            variant="caption"
                                            className={classes.forgotWith2FA}
                                            onClick={handleForgotClick}
                                        >
                                            <Link to={getParsedPath(ROUTES.USER.FORGOT_PASSWORD)}>
                                                {t('BUTTON_FORGOTTEN')}
                                            </Link>
                                        </Typography>
                                    </Grid>
                                )}
                                {!!errorMessage && (
                                    <Grid size={12}>
                                        <Typography color="error">{errorMessage}</Typography>
                                    </Grid>
                                )}
                                <Grid size={12}>
                                    <LoadingButton
                                        variant="contained"
                                        color="primary"
                                        disabled={
                                            !isValid ||
                                            isAuthLoading ||
                                            isIdentityEmpty ||
                                            (!has2FA && !password)
                                        }
                                        fullWidth
                                        onClick={submitForm}
                                        className={classes.button}
                                        loading={isSubmitting || isAuthLoading}
                                    >
                                        {t('BUTTON_SUBMIT')}
                                    </LoadingButton>
                                </Grid>
                                <Grid size={12}>
                                    <TermsAndConditions />
                                </Grid>
                            </StyledGrid>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
};
